import { Link } from "react-router-dom";
import { MapPin, Maximize2, ShieldCheck, Star, BadgeCheck, GraduationCap } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { fmtFJD, fileUrl } from "@/lib/api";

export default function PropertyCard({ listing }) {
    const photo =
        listing.external_photos?.[0] ||
        (listing.photos?.[0] ? fileUrl(listing.photos[0]) : null) ||
        "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=800&q=80";

    const isJv = listing.jv_eligible || listing.listing_type === "jv" || listing.listing_type === "both";

    return (
        <Link to={`/properties/${listing.id}`} data-testid={`property-card-${listing.id}`} className="group">
            <Card className="overflow-hidden border border-border hover:shadow-[0_8px_30px_rgba(10,61,98,0.18)] transition-all duration-300 hover:-translate-y-1 bg-card">
                <div className="relative aspect-[16/10] overflow-hidden">
                    <img
                        src={photo}
                        alt={listing.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    {listing.is_featured && (
                        <div data-testid={`featured-ribbon-${listing.id}`} className="absolute top-0 right-0 bg-coral text-white px-3 py-1 text-[10px] font-app font-semibold uppercase tracking-wider rounded-bl-md flex items-center gap-1 shadow-md">
                            <Star className="h-3 w-3 fill-white" /> Featured
                        </div>
                    )}
                    <div className="absolute top-3 left-3 flex gap-2 flex-wrap">
                        {isJv && (
                            <Badge className="bg-coral text-white border-0 font-app uppercase tracking-wider text-[10px]">Joint Venture Available</Badge>
                        )}
                        <Badge className="bg-ocean text-white border-0 font-app uppercase tracking-wider text-[10px]">{listing.location_island}</Badge>
                    </div>
                    {listing.owner_agent_trained && (
                        <div
                            data-testid={`certified-badge-${listing.id}`}
                            className="absolute bottom-3 left-3 bg-coral text-white px-2 py-1 rounded-md font-app font-semibold text-[10px] uppercase tracking-wider flex items-center gap-1 shadow-md"
                            title="Certified Bula Agent"
                        >
                            <GraduationCap className="h-3 w-3" /> Certified Agent
                        </div>
                    )}
                    <div className="absolute bottom-3 right-3 bg-white/95 dark:bg-card/95 backdrop-blur px-3 py-1 rounded-md font-display font-bold text-ocean dark:text-turquoise">
                        {fmtFJD(listing.price_fjd)}
                    </div>
                </div>
                <div className="p-5">
                    <div className="flex items-center gap-1 text-xs text-muted-foreground font-app mb-1">
                        <MapPin className="h-3 w-3" />
                        {listing.location_region}
                    </div>
                    <h3 className="font-display text-lg font-semibold leading-tight line-clamp-2 mb-2 group-hover:text-ocean dark:group-hover:text-turquoise transition-colors">
                        {listing.title}
                    </h3>
                    {listing.owner_is_agent && listing.owner_agency_name && (
                        <div className="flex items-center gap-1 text-[11px] font-app text-muted-foreground mb-2">
                            <span className="truncate">via {listing.owner_agency_name}</span>
                            {listing.owner_agent_verified && (
                                <span className="text-fiji-green flex items-center" title="Verified Agent">
                                    <BadgeCheck className="h-3 w-3" />
                                </span>
                            )}
                            {listing.owner_agent_trained && (
                                <span className="text-coral flex items-center" title="Certified Bula Agent">
                                    <GraduationCap className="h-3 w-3" />
                                </span>
                            )}
                        </div>
                    )}
                    <div className="flex items-center justify-between text-xs font-app text-muted-foreground">
                        <span className="flex items-center gap-1"><Maximize2 className="h-3 w-3" /> {listing.land_size_acres} acres</span>
                        <span className="capitalize">{listing.zoning}</span>
                        <span className="flex items-center gap-1 text-fiji-green"><ShieldCheck className="h-3 w-3" /> Verified</span>
                    </div>
                </div>
            </Card>
        </Link>
    );
}

